import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover
} from '@clientbase/clientbase-library'
import {
  Box,
  IconButton,
  MenuItem,
  Skeleton as MuiSkeleton,
  Stack
} from '@mui/material'
import {
  resyncAccountsPayableRecurrenceContaAzul,
  syncAccountsPayableContaAzul
} from 'api/contaAzul'
import { DataService } from 'api/DataService'
import { usePermission } from 'hooks/usePermission'
import { useActiveMerchantForRecord } from 'lib/merchant-group'
import {
  AccountsPayable,
  AccountsPayableItem,
  AccountsPayablePayee,
  constructorAccountsPayable,
  constructorRecurrence,
  RecurrenceAccountsPayable
} from 'models/AccountsPayable'
import {
  ExpenseModalProps,
  useAccountsPayableContext
} from 'templates/AccountsPayable/context'

import Can from 'components/Can'
import ContaAzulRecurrenceTable, {
  ContaAzulResyncFooter,
  hasContaAzulInfo
} from 'components/ContaAzulRecurrenceAccordion'
import ModalContaAzulDetail from 'components/Modal/ModalContaAzulDetail'
import { mapExpenseToDuplicateFormValues } from 'components/Modal/ModalNewExpense/duplicate.utils'
import TagsPopover from 'components/TagsPopover'

import ExpensesAccordion from './components/Accordion'
import Actions from './components/Actions'
import ContaAzulSection from './components/Sessions/ContaAzulSection'
import ExpenseAllocations from './components/Sessions/ExpenseAllocations'
import ExpenseBeneficiary from './components/Sessions/ExpenseBeneficiary'
import ExpenseComposition from './components/Sessions/ExpenseComposition'
import ExpenseDetailsPayment from './components/Sessions/ExpenseDetailsPayment'
import ExpensesCicles from './components/Sessions/ExpensesCicles'
import ExpensesDetailsContent from './components/Sessions/ExpensesDetailsContent'
import Skeleton from './components/Skeleton/Skeleton'
import Title from './components/Title'
import * as S from './styles'
interface ModalDetailsExpensesProps {
  isOpen: boolean
  handleClose: Dispatch<SetStateAction<ExpenseModalProps>>
  uuid: string
}
export type RecurrenceExpensesDetails = {
  recurrenceData: AccountsPayable
  recurrenceDetails: RecurrenceAccountsPayable[]
  status: string
}

type TagFormData = {
  tags: Array<{
    id: string
    name: string
    color?: string
  }>
}

const ModalDetailsExpenses = ({
  handleClose,
  isOpen,
  uuid
}: ModalDetailsExpensesProps) => {
  // SC-10999: aberto de uma lista agregada do grupo (contas a pagar, fluxo de
  // caixa), busca e age na empresa da despesa
  useActiveMerchantForRecord(isOpen ? uuid : undefined)
  const {
    reloadTable,
    setReloadTable,
    setExpenseDetailModal,
    expenseDetailModal,
    setExpenseFormModal
  } = useAccountsPayableContext()
  const [expenseDetails, setExpenseDetails] = useState<AccountsPayable>()
  const [isRecurrent, setIsRecurrent] = useState<boolean>(
    !!expenseDetailModal.isRecurrence
  )
  const [recurrenceDetails, setRecurrenceDetails] =
    useState<RecurrenceExpensesDetails>()
  const [nestedModalCycles, setNestedModalCycles] = useState<boolean>(false)

  const [visitedCycle, setVisitedCycle] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [syncingApUuid, setSyncingApUuid] = useState<string | null>(null)
  const [resyncingApRecurrence, setResyncingApRecurrence] =
    useState<boolean>(false)
  // Reenviar despesa para a Conta Azul e acao de contas a pagar, nao de integracao:
  // o backend exige accounts_payable:manage (conta_azul/accounts_payables#sync e
  // #retry_recurrence).
  const canManageContaAzulAp = usePermission('accounts_payable:manage')

  const handleSyncAp = async (accountsPayableUuid?: string) => {
    if (!accountsPayableUuid) return
    try {
      setSyncingApUuid(accountsPayableUuid)
      await syncAccountsPayableContaAzul(accountsPayableUuid)
      toast.success('Sincronização com a Conta Azul enfileirada.')
      await getDetailsRecurrenceExpenses()
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao sincronizar com a Conta Azul.')
    } finally {
      setSyncingApUuid(null)
    }
  }

  // Reenvia todos os ciclos da recorrência de uma vez (notificação in-app consolidada).
  const handleResyncApRecurrence = async () => {
    const recurrenceUuid = recurrenceDetails?.recurrenceData?.uuid
    if (!recurrenceUuid) return
    try {
      setResyncingApRecurrence(true)
      await resyncAccountsPayableRecurrenceContaAzul(recurrenceUuid)
      toast.success(
        'Sincronização da recorrência com a Conta Azul enfileirada.'
      )
      await getDetailsRecurrenceExpenses()
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao sincronizar com a Conta Azul.')
    } finally {
      setResyncingApRecurrence(false)
    }
  }

  const contaAzulRecurrenceRows = (
    recurrenceDetails?.recurrenceDetails || []
  ).map((cycle) => ({
    uuid: cycle.uuid,
    cycle: cycle.recurrenceCycle ?? undefined,
    dueDate: cycle.dueDate,
    amount: cycle.amountBilled,
    contaAzulInfo: cycle.contaAzulInfo
  }))

  // Estados separados para tags de despesa e recorrência
  const [expenseTags, setExpenseTags] = useState<
    Array<{
      id: string
      name: string
      color?: string
    }>
  >([])

  const [recurrenceTags, setRecurrenceTags] = useState<
    Array<{
      id: string
      name: string
      color?: string
    }>
  >([])

  // Form para gerenciar tags - será atualizado dinamicamente baseado no contexto
  const { control, setValue } = useForm<TagFormData>({
    defaultValues: {
      tags: []
    }
  })
  const getDetailsExpenses = async (uuid: string) => {
    if (!nestedModalCycles) {
      setIsRecurrent(false)
    }
    setLoading(true)
    const response = await DataService({
      url: `/v1/accounts_payables/${uuid}`,
      type: 'GET'
    })
    const expenseData = constructorAccountsPayable(response.data.record)
    setExpenseDetails(expenseData)

    // Salvar tags da despesa no estado separado
    const tags = expenseData.tags || []
    setExpenseTags(tags)

    // Atualizar o form apenas se não estamos em recorrência
    if (!isRecurrent) {
      setValue('tags', tags)
    }

    setLoading(false)
  }

  const getDetailsRecurrenceExpenses = async () => {
    setIsRecurrent(true)
    setLoading(true)
    const response = await DataService({
      url: `v1/accounts_payables_recurrences/${
        expenseDetailModal.isRecurrence
          ? uuid
          : expenseDetails?.recurrenceUuid
          ? expenseDetails?.recurrenceUuid
          : expenseDetailModal.uuid
      }`,
      type: 'GET'
    })

    const recrurrenceData = constructorAccountsPayable(response.data.record)
    setRecurrenceDetails({
      recurrenceDetails: response?.data?.record?.accounts_payables?.map(
        (item: RecurrenceAccountsPayable) => constructorRecurrence(item)
      ),
      recurrenceData: recrurrenceData,
      status: response.data?.record?.status
    })

    // Salvar tags da recorrência no estado separado
    const tags = recrurrenceData.tags || []
    setRecurrenceTags(tags)

    // Atualizar o form com as tags da recorrência
    setValue('tags', tags)

    setExpenseDetailModal({
      isOpen: true,
      uuid: expenseDetailModal.isRecurrence
        ? uuid
        : expenseDetails?.recurrenceUuid
    })
    setReloadTable(reloadTable)
    setLoading(false)
  }

  useEffect(() => {
    setVisitedCycle(false)
    // Reset tags when modal closes
    if (!isOpen) {
      setValue('tags', [])
      setExpenseTags([])
      setRecurrenceTags([])
    }
  }, [isOpen, setValue])

  // Update form tags when switching between expense and recurrence
  useEffect(() => {
    if (isRecurrent) {
      setValue('tags', recurrenceTags)
    } else {
      setValue('tags', expenseTags)
    }
  }, [isRecurrent, expenseTags, recurrenceTags, setValue])

  useEffect(() => {
    if (nestedModalCycles) {
      getDetailsExpenses(uuid)
    } else if (
      (recurrenceDetails &&
        recurrenceDetails.recurrenceData &&
        recurrenceDetails.recurrenceData.uuid) ||
      expenseDetailModal.isRecurrence
    ) {
      getDetailsRecurrenceExpenses()
    } else {
      getDetailsExpenses(uuid)
    }
  }, [reloadTable])

  const handleDuplicate = () => {
    if (!expenseDetails) return
    const defaultValues = mapExpenseToDuplicateFormValues(expenseDetails)
    setExpenseDetailModal({ isOpen: false, uuid: '' })
    setExpenseFormModal({
      isOpen: true,
      uuid: '',
      isRecurrence: false,
      defaultValues,
      sourceUuid: expenseDetails.uuid || uuid
    })
  }

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [caOpen, setCaOpen] = useState<boolean>(false)
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }
  const handleCloseMenu = () => setMenuAnchorEl(null)
  const isRecurrenceView = !!recurrenceDetails && !nestedModalCycles
  const showDuplicateMenu =
    !isRecurrenceView &&
    !!expenseDetails &&
    expenseDetails.status !== 'cancelled'
  // Conta Azul virou uma opção do menu "..." e segue o mesmo padrão do modal de recorrência
  // de billings: na visão da recorrência exibe a tabela por ciclo; na visão de um ciclo (ou
  // despesa avulsa) exibe o status daquele ciclo. Os dois casos são mutuamente exclusivos.
  const hasCaRecurrence =
    isRecurrenceView && hasContaAzulInfo(contaAzulRecurrenceRows)
  const hasCaRecord = !isRecurrenceView && !!expenseDetails?.contaAzulInfo
  const hasCa = hasCaRecurrence || hasCaRecord
  const showMenu = showDuplicateMenu || hasCa

  const currentExpense =
    recurrenceDetails && isRecurrent && !nestedModalCycles
      ? recurrenceDetails.recurrenceData
      : expenseDetails
  const expenseAllocations = currentExpense?.allocations ?? []

  const handleCloseModal = () => {
    if (!expenseDetails) {
      setNestedModalCycles(false)
      handleClose({ isOpen: false, uuid: '' })
      return
    }

    if (nestedModalCycles) {
      setVisitedCycle(true)
      setNestedModalCycles(false)
      setIsRecurrent(true)
      getDetailsRecurrenceExpenses()
      return
    }

    if (
      recurrenceDetails &&
      recurrenceDetails.recurrenceData &&
      recurrenceDetails.recurrenceData.uuid
    ) {
      if (visitedCycle) {
        setNestedModalCycles(false)
        handleClose({ isOpen: false, uuid: '' })
      } else {
        // Voltando da recorrência para a despesa - restaurar tags da despesa
        setIsRecurrent(false)
        setRecurrenceDetails(undefined)
        setValue('tags', expenseTags) // Restaurar tags da despesa
        setExpenseDetailModal({
          isOpen: true,
          uuid: expenseDetails.uuid
        })
      }
    } else {
      setNestedModalCycles(false)
      handleClose({ isOpen: false, uuid: '' })
    }
  }
  return (
    <Modal
      handleClose={() => handleCloseModal()}
      open={isOpen}
      width="100%"
      maxWidth="80vw"
      maxHeight="90vh"
      height="94vh"
    >
      <ModalBody>
        <ModalHeader onClose={handleCloseModal}>
          <Stack
            width="100%"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Title
              isRecurrent={isRecurrent}
              recurrenceDetails={recurrenceDetails}
              getDetailsRecurrenceExpenses={getDetailsRecurrenceExpenses}
              uuid={
                recurrenceDetails && !nestedModalCycles
                  ? recurrenceDetails?.recurrenceData?.uuid || uuid
                  : expenseDetails?.uuid || uuid
              }
              expenseDetails={expenseDetails}
              nestedModalCycles={nestedModalCycles}
              loading={loading}
              status={
                recurrenceDetails && !nestedModalCycles
                  ? recurrenceDetails?.status
                  : expenseDetails?.status || ''
              }
            />
            {showMenu && (
              <Can permission="accounts_payable:manage">
                <IconButton
                  onClick={handleOpenMenu}
                  size="small"
                  sx={{ mr: '8px' }}
                >
                  <Icon icon="moreHoriz" colorSX="black" />
                </IconButton>
              </Can>
            )}
          </Stack>
        </ModalHeader>

        <Popover
          anchorEl={menuAnchorEl}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          onClose={handleCloseMenu}
          open={Boolean(menuAnchorEl)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200 }}>
            {showDuplicateMenu && (
              <MenuItem
                onClick={() => {
                  handleCloseMenu()
                  handleDuplicate()
                }}
                sx={{
                  display: 'flex',
                  fontSize: 14,
                  color: '#646464',
                  gap: '8px',
                  py: '6px',
                  borderRadius: '6px'
                }}
              >
                <Icon icon="contentCopy" colorSX="#000" />
                Duplicar
              </MenuItem>
            )}
            {hasCa && (
              <MenuItem
                onClick={() => {
                  handleCloseMenu()
                  setCaOpen(true)
                }}
                sx={{
                  display: 'flex',
                  fontSize: 14,
                  color: '#646464',
                  gap: '8px',
                  py: '6px',
                  borderRadius: '6px'
                }}
              >
                <Icon icon="link" colorSX="#000" />
                Conta Azul
              </MenuItem>
            )}
          </Box>
        </Popover>

        {hasCa && (
          <ModalContaAzulDetail
            open={caOpen}
            onClose={() => setCaOpen(false)}
            title="Conta Azul"
          >
            {hasCaRecurrence ? (
              <>
                <ModalContent>
                  <ContaAzulRecurrenceTable
                    records={contaAzulRecurrenceRows}
                    syncingUuid={syncingApUuid}
                    onSync={handleSyncAp}
                    canManage={canManageContaAzulAp}
                  />
                </ModalContent>
                <ContaAzulResyncFooter
                  records={contaAzulRecurrenceRows}
                  onResync={handleResyncApRecurrence}
                  resyncing={resyncingApRecurrence}
                  canManage={canManageContaAzulAp}
                />
              </>
            ) : (
              expenseDetails?.contaAzulInfo && (
                <ContaAzulSection
                  info={expenseDetails.contaAzulInfo}
                  onRefresh={() =>
                    getDetailsExpenses(expenseDetails?.uuid || uuid)
                  }
                />
              )
            )}
          </ModalContaAzulDetail>
        )}

        <ModalContent>
          {loading ? (
            <Skeleton />
          ) : (
            <Box
              mb={!recurrenceDetails && !nestedModalCycles ? '100px' : '0px'}
            >
              <S.Wrapper>
                <S.Session>
                  <ExpensesDetailsContent
                    description={expenseDetails?.description}
                    documentFile={expenseDetails?.documentFile}
                    documentFiles={expenseDetails?.documentFiles}
                    isRecurrent={nestedModalCycles ? false : isRecurrent}
                    getDetailsRecurrenceExpenses={getDetailsRecurrenceExpenses}
                    uuid={uuid}
                    expenseDetails={
                      recurrenceDetails && isRecurrent && !nestedModalCycles
                        ? recurrenceDetails?.recurrenceData
                        : (expenseDetails as AccountsPayable)
                    }
                  />
                </S.Session>
              </S.Wrapper>
              <Stack mt="10px" gap="10px">
                {expenseAllocations.length > 1 && (
                  <ExpensesAccordion icon="compareArrows" text="Rateio">
                    <ExpenseAllocations allocations={expenseAllocations} />
                  </ExpensesAccordion>
                )}
                {(!!expenseDetails?.items ||
                  (!!recurrenceDetails && !nestedModalCycles)) && (
                  <ExpensesAccordion icon="paid" text="Composição da despesa">
                    <ExpenseComposition
                      items={
                        recurrenceDetails && !nestedModalCycles
                          ? recurrenceDetails.recurrenceData.items
                          : (expenseDetails?.items as AccountsPayableItem[])
                      }
                    />
                  </ExpensesAccordion>
                )}

                <ExpensesAccordion
                  defaultExpanded={
                    expenseDetails?.status !== 'paid' ? true : false
                  }
                  icon="supervisorAccount"
                  text="Detalhes do beneficiário"
                >
                  <ExpenseBeneficiary
                    payee={
                      recurrenceDetails && !nestedModalCycles
                        ? (recurrenceDetails?.recurrenceData
                            .payee as AccountsPayablePayee)
                        : (expenseDetails?.payee as AccountsPayablePayee)
                    }
                  />
                </ExpensesAccordion>

                {expenseDetails?.status === 'paid' && (
                  <ExpensesAccordion
                    defaultExpanded={false}
                    icon="receipt"
                    text="Detalhes do pagamento"
                  >
                    <ExpenseDetailsPayment
                      payment_source_name={expenseDetails?.source?.name || ''}
                      amountPaid={expenseDetails?.amountPaid || ''}
                      datePaid={expenseDetails?.datePaid || ''}
                      uuid={expenseDetailModal.uuid}
                    />
                  </ExpensesAccordion>
                )}

                {recurrenceDetails && isRecurrent && !nestedModalCycles && (
                  <ExpensesAccordion icon="cached" text="Ciclos">
                    <ExpensesCicles
                      setNestedModalCycles={setNestedModalCycles}
                      getDetailsExpenses={getDetailsExpenses}
                      data={recurrenceDetails}
                    />
                  </ExpensesAccordion>
                )}
              </Stack>
            </Box>
          )}
        </ModalContent>

        <ModalFooter>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            width="100%"
            gap={1}
          >
            {/* TagsPopover and Actions grouped together on the right */}
            {loading ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <MuiSkeleton
                  variant="rounded"
                  width={36}
                  height={36}
                  animation="wave"
                  sx={{ borderRadius: '8px' }}
                />
                <MuiSkeleton
                  variant="rounded"
                  width={80}
                  height={36}
                  animation="wave"
                  sx={{ borderRadius: '8px' }}
                />
                <MuiSkeleton
                  variant="rounded"
                  width={80}
                  height={36}
                  animation="wave"
                  sx={{ borderRadius: '8px' }}
                />
                <MuiSkeleton
                  variant="rounded"
                  width={140}
                  height={36}
                  animation="wave"
                  sx={{ borderRadius: '8px' }}
                />
              </Stack>
            ) : (
              <Can permission="accounts_payable:manage">
                <Stack direction="row" alignItems="center" gap={1}>
                  <TagsPopover
                    control={control}
                    name="tags"
                    saveMode="immediate"
                    saveContext={{
                      taggableType:
                        isRecurrent && recurrenceDetails
                          ? 'AccountsPayableRecurrence'
                          : 'AccountsPayable',
                      taggableUuid:
                        isRecurrent && recurrenceDetails
                          ? recurrenceDetails.recurrenceData.uuid
                          : expenseDetails?.uuid || ''
                    }}
                    onSave={async (tags) => {
                      // Update local state with new tags
                      const updatedTags = tags.map((tag) => ({
                        id: tag.id,
                        name: tag.name,
                        color: tag.color || '#000000'
                      }))

                      // Update the appropriate state based on context
                      if (isRecurrent && recurrenceDetails) {
                        // Update recurrence tags
                        setRecurrenceTags(updatedTags)
                        setRecurrenceDetails((prev) =>
                          prev
                            ? {
                                ...prev,
                                recurrenceData: {
                                  ...prev.recurrenceData,
                                  tags: updatedTags
                                }
                              }
                            : undefined
                        )
                      } else if (expenseDetails) {
                        // Update expense tags
                        setExpenseTags(updatedTags)
                        setExpenseDetails((prev) =>
                          prev
                            ? {
                                ...prev,
                                tags: updatedTags
                              }
                            : undefined
                        )
                      }

                      // Show success notification
                      toast.success('Tags atualizadas com sucesso!')
                    }}
                    maxTags={10}
                  />
                  <Actions
                    hasPaidExpensesAssociated={recurrenceDetails?.recurrenceDetails?.some(
                      (item) => item.status === 'paid'
                    )}
                    cycle={expenseDetails?.recurrenceCycle}
                    isRecurrence={!!recurrenceDetails && !nestedModalCycles}
                    dueDate={expenseDetails?.dueDate}
                    status={
                      recurrenceDetails && !nestedModalCycles
                        ? recurrenceDetails?.status
                        : expenseDetails?.status || ''
                    }
                    amount={
                      nestedModalCycles
                        ? expenseDetails?.amountBilled?.toString() || ''
                        : recurrenceDetails && !nestedModalCycles
                        ? recurrenceDetails?.recurrenceData?.amountBilled?.toString() ||
                          ''
                        : expenseDetails?.amountBilled?.toString() || ''
                    }
                    uuid={uuid}
                  />
                </Stack>
              </Can>
            )}
          </Stack>
        </ModalFooter>
      </ModalBody>
    </Modal>
  )
}

export default ModalDetailsExpenses
